/**
 * CostSalaryBars — compact horizontal bar chart used inside each
 * country tile on the cross-country compare view.
 *
 * Two stacked bars:
 *   • Full-degree international cost (dark stone)
 *   • Y1 median starting salary (emerald)
 *
 * Bars share a common `maxUsd` so they're comparable across all five
 * country tiles at a glance — users can see immediately whether the
 * salary bar overtakes the cost bar (i.e. cost paid back in Y1) or
 * lags behind by a wide margin.
 *
 * Renders `null` when both values are missing so tiles with no data
 * don't leave a hollow section.
 */
export default function CostSalaryBars({
  costUsd,
  salaryUsd,
  maxUsd,
  formatCurrency,
  testid,
}) {
  const nCost = typeof costUsd === "number" ? costUsd : null;
  const nSalary = typeof salaryUsd === "number" ? salaryUsd : null;
  if (nCost == null && nSalary == null) return null;

  const scale = maxUsd && maxUsd > 0 ? maxUsd : Math.max(nCost || 0, nSalary || 0, 1);
  const costPct = nCost ? Math.min(100, (nCost / scale) * 100) : 0;
  const salaryPct = nSalary ? Math.min(100, (nSalary / scale) * 100) : 0;

  // A single scalar the user can act on: how many years of salary would
  // repay the sticker cost? (Cost / Salary). Only shown when both known.
  let payback = null;
  if (nCost && nSalary && nSalary > 0) {
    payback = (nCost / nSalary).toFixed(1);
  }

  return (
    <div className="border-t border-stone-100 pt-3" data-testid={testid || "cost-salary-bars"}>
      <div className="flex items-center justify-between">
        <div className="label-uppercase !text-[9px] text-stone-500">Cost vs salary</div>
        {payback && (
          <div
            className="text-[9px] font-underground-sc tracking-wider text-stone-500"
            title="Years of Y1 salary to repay the full international tuition (excluding living costs)."
          >
            {payback}y payback
          </div>
        )}
      </div>
      <div className="mt-2 space-y-1.5">
        <BarRow label="Cost" value={nCost} pct={costPct} tone="bg-stone-700" formatCurrency={formatCurrency} />
        <BarRow label="Y1 sal" value={nSalary} pct={salaryPct} tone="bg-emerald-500" formatCurrency={formatCurrency} />
      </div>
    </div>
  );
}


function BarRow({ label, value, pct, tone, formatCurrency }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 text-[9px] font-underground-sc tracking-wider text-stone-500 shrink-0">
        {label}
      </div>
      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${tone} rounded-full transition-[width] duration-500`}
          style={{ width: `${pct}%` }}
          aria-hidden="true"
        />
      </div>
      <div className="text-[10px] font-mono text-stone-700 shrink-0 min-w-[52px] text-right">
        {typeof value === "number" ? formatCurrency(value) : "—"}
      </div>
    </div>
  );
}
